import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Download, X } from "lucide-react";
import { toast } from "sonner";
import { sendForm } from "../lib/forms";

export default function CheckoutModal({ product, onClose }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const downloadStarter = (p) => {
    const body = [
      `${p.name} — Starter pages`,
      `Back of the Mind · ${p.kind}`,
      "",
      p.description,
      "",
      "This starter file contains your first pages. The full file unlocks",
      "with your order — check your inbox.",
      "",
      "Look inward. Understand what shapes you.",
    ].join("\n");
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${p.slug}-starter.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buy = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("A name and email is all we need.");
      return;
    }
    setSending(true);
    try {
      await sendForm(`New order — ${product.name}`, {
        name: form.name,
        email: form.email,
        product: product.name,
        price: `$${product.price.toFixed(0)} USD`,
      });
      setDone(true);
      downloadStarter(product);
      toast.success(`${product.name} order placed. Starter pages downloaded.`);
    } catch {
      toast.error("Couldn't place the order right now — please email connectbackofthemind@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label="Checkout"
        >
          <div
            data-testid="checkout-backdrop"
            className="absolute inset-0 bg-pinedeep/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            data-testid="checkout-modal"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-t-[2rem] bg-cream p-7 shadow-2xl sm:rounded-[2rem]"
          >
            <button
              data-testid="checkout-close"
              onClick={onClose}
              aria-label="Close checkout"
              className="absolute right-5 top-5 rounded-full border border-pine/15 p-2 text-pine hover:bg-sagelight/60"
            >
              <X className="h-4 w-4" />
            </button>

            {done ? (
              <div data-testid="checkout-success" className="py-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sagelight text-pine">
                  <Check className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-serif text-2xl text-ink">{product.name} is yours.</h3>
                <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-bodytext">
                  Your starter pages just downloaded, and we'll email the full file to{" "}
                  <strong>{form.email}</strong>. Downloads never expire.
                </p>
                <button
                  data-testid="checkout-success-download"
                  onClick={() => downloadStarter(product)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-semibold text-cream hover:bg-pinedeep"
                >
                  <Download className="h-4 w-4" /> Download again
                </button>
              </div>
            ) : (
              <>
                <p className="eyebrow">Checkout</p>
                <h3 className="mt-2 font-serif text-2xl text-ink">{product.name}</h3>
                <p className="mt-1 text-sm text-bodytext/70">
                  {product.kind} · ${product.price.toFixed(0)} USD · instant download
                </p>
                <div className="mt-5 space-y-3">
                  <input
                    data-testid="checkout-name-input"
                    className="input-soft"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  <input
                    data-testid="checkout-email-input"
                    className="input-soft"
                    type="email"
                    placeholder="Email for your download"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <button
                  data-testid="checkout-submit-button"
                  disabled={sending}
                  onClick={buy}
                  className="mt-6 w-full rounded-full bg-terra py-3.5 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:brightness-105 disabled:opacity-60"
                >
                  {sending ? "Processing…" : `Place order — $${product.price.toFixed(0)}, get it instantly`}
                </button>
                <p className="mt-3 text-center text-[11px] text-bodytext/50">
                  14-day goodwill refund · files are yours for keeps
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
